import { useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  DEVICE_SOURCE_OPTIONS,
  LOAD_STATUS_OPTIONS,
  PARTNER_OPTIONS,
  PRIORITY_OPTIONS,
  SEARCH_KEY_OPTIONS,
  SERVICE_CATEGORY_LABEL,
  SERVICE_CATEGORY_OPTIONS,
  USER_STATUS,
  USER_STATUS_OPTIONS,
  mapLoadStatus,
} from '@/shared/constants/users';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { MultiSelect } from '@/shared/ui/MultiSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { UserProfileRedirect } from '@/shared/ui/UserProfileRedirect';
import {
  fetchCities,
  fetchCountries,
  fetchRegions,
  fetchRoles,
  fetchUsersList,
  fetchUtmList,
  showUserPhone,
} from './api';

interface RoleRef {
  value?: string;
  label?: string;
}

interface PlaceRef {
  name?: string;
}

interface CarRef {
  mark?: { name?: string };
  model?: { name?: string };
}

export interface UserRow extends Record<string, unknown> {
  id: number | string;
  name?: string;
  email?: string;
  phone?: string | null;
  balance?: number | string;
  roles?: RoleRef[];
  status_id?: number;
  create_time?: number | string;
  country?: PlaceRef;
  region?: PlaceRef;
  city?: PlaceRef;
  cars?: CarRef[];
  source?: string;
  partner_name?: string;
  manager?: { name?: string } | null;
  is_priority?: number | boolean;
  utm_source?: string;
  rate?: string | number;
  category?: number;
}

const emptyFilters = {
  searchKey: '',
  searchValue: '',
  statusId: '',
  roleIds: [] as string[],
  countryId: '1',
  regionId: '',
  cityId: '',
  source: '',
  priority: '',
  category: '',
  utmId: '',
  partnerId: '',
  loadStatus: '',
};

type Filters = typeof emptyFilters;

const labelStyle: CSSProperties = { display: 'block', fontWeight: 600, marginBottom: 4 };

const formatPlace = (u: UserRow) =>
  [u.country?.name, u.city?.name, u.region?.name].filter(Boolean).join(', ');

const formatCar = (u: UserRow) => {
  const car = u.cars?.[0];
  if (!car) return '';
  return `${car.mark?.name || ''} ${car.model?.name || ''}`.trim();
};

const formatRoles = (roles?: RoleRef[]) =>
  Array.isArray(roles) && roles.length ? roles.map((r) => r.label || r.value).join(' ') : '';

export const UsersPage = () => {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('-id');
  const [draft, setDraft] = useState<Filters>({ ...emptyFilters });
  const [applied, setApplied] = useState<Filters>({ ...emptyFilters });
  const [phones, setPhones] = useState<Record<string, string>>({});

  const countriesQuery = useQuery({ queryKey: ['countries'], queryFn: fetchCountries });
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: fetchRoles });
  const utmQuery = useQuery({ queryKey: ['utm-list'], queryFn: fetchUtmList });
  const regionsQuery = useQuery({
    queryKey: ['regions', draft.countryId],
    queryFn: () => fetchRegions(draft.countryId),
    enabled: Boolean(draft.countryId),
  });
  const citiesQuery = useQuery({
    queryKey: ['cities', draft.regionId],
    queryFn: () => fetchCities(draft.regionId),
    enabled: Boolean(draft.regionId),
  });

  const listParams = useMemo(() => {
    const { isParsed, isVerified } = mapLoadStatus(applied.loadStatus);
    return {
      page,
      sort_field: sortField,
      id: applied.searchKey === 'id' ? applied.searchValue : '',
      name: applied.searchKey === 'name' ? applied.searchValue : '',
      phone: applied.searchKey === 'phone' ? applied.searchValue : '',
      email: applied.searchKey === 'email' ? applied.searchValue : '',
      status_id: applied.statusId,
      roles: applied.roleIds.length ? applied.roleIds : undefined,
      country_id: applied.countryId,
      region_id: applied.regionId,
      city_id: applied.cityId,
      source: applied.source,
      partner_id: applied.partnerId,
      price_template_is_parsed: isParsed,
      price_template_is_verified: isVerified,
      is_priority: applied.priority,
      utm_id: applied.utmId,
      category: applied.category,
    };
  }, [applied, page, sortField]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['users', listParams],
    queryFn: () => fetchUsersList(listParams),
  });

  const rows = getListRows<UserRow>(data, ['users', 'items']);
  const totalPages = getTotalPages(data);

  const phoneMutation = useMutation({
    mutationFn: (id: string | number) => showUserPhone(id),
    onSuccess: (res, id) => {
      if (res.success === 1 && res.phone) {
        setPhones((prev) => ({ ...prev, [String(id)]: res.phone! }));
        void qc.invalidateQueries({ queryKey: ['users'] });
      }
    },
  });

  const setDraftField = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const toggleSort = (field: string) => {
    setSortField((prev) => (prev === field ? `-${field}` : prev === `-${field}` ? field : `-${field}`));
    setPage(1);
  };

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    setApplied({ ...draft });
  };

  const onReset = (e: FormEvent) => {
    e.preventDefault();
    const next = { ...emptyFilters };
    setDraft(next);
    setApplied(next);
    setPage(1);
    setSortField('-id');
  };

  const toOptions = (items: Array<{ id: string | number; name?: string; title?: string }> = []) =>
    items.map((item) => ({ value: String(item.id), label: String(item.name ?? item.title ?? item.id) }));

  const roleOptions = toOptions(rolesQuery.data);

  const sortClass = (field: string) =>
    sortField === field || sortField === `-${field}` ? 'active' : '';

  return (
    <>
      <section className="content-header">
        <h1>Пользователи</h1>
        <div className="row">
          <div className="col-sm-12">
            <Link to="/users/create" className="btn btn-info pull-right">
              Создать
            </Link>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Список пользователей</h3>
            <form onSubmit={onSearch} style={{ marginTop: 12 }}>
              <div className="row">
                <div className="col-lg-4">
                  <div className="form-group">
                    <span style={labelStyle}>Поиск</span>
                    <FilterSelect
                      options={SEARCH_KEY_OPTIONS}
                      value={draft.searchKey}
                      onChange={(searchKey) => setDraftField('searchKey', searchKey)}
                    />
                    <input
                      className="form-control input-sm"
                      style={{ marginTop: 6, width: 250 }}
                      placeholder="Поиск..."
                      value={draft.searchValue}
                      onChange={(e) => setDraftField('searchValue', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по статусу</span>
                    <FilterSelect
                      options={USER_STATUS_OPTIONS}
                      value={draft.statusId}
                      onChange={(statusId) => setDraftField('statusId', statusId)}
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по роли</span>
                    <MultiSelect
                      options={roleOptions}
                      value={draft.roleIds}
                      onChange={(roleIds) => setDraftField('roleIds', roleIds)}
                      isLoading={rolesQuery.isLoading}
                    />
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по стране</span>
                    <FilterSelect
                      options={toOptions(countriesQuery.data)}
                      value={draft.countryId}
                      isLoading={countriesQuery.isLoading}
                      onChange={(countryId) =>
                        setDraft((p) => ({ ...p, countryId, regionId: '', cityId: '' }))
                      }
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по региону</span>
                    <FilterSelect
                      options={toOptions(regionsQuery.data)}
                      value={draft.regionId}
                      isDisabled={!draft.countryId}
                      isLoading={regionsQuery.isFetching}
                      onChange={(regionId) => setDraft((p) => ({ ...p, regionId, cityId: '' }))}
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по городу</span>
                    <FilterSelect
                      options={toOptions(citiesQuery.data)}
                      value={draft.cityId}
                      isDisabled={!draft.regionId}
                      isLoading={citiesQuery.isFetching}
                      onChange={(cityId) => setDraftField('cityId', cityId)}
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по устройству</span>
                    <FilterSelect
                      options={DEVICE_SOURCE_OPTIONS}
                      value={draft.source}
                      onChange={(source) => setDraftField('source', source)}
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по приоритету</span>
                    <FilterSelect
                      options={PRIORITY_OPTIONS}
                      value={draft.priority}
                      onChange={(priority) => setDraftField('priority', priority)}
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по типу СТО</span>
                    <FilterSelect
                      options={SERVICE_CATEGORY_OPTIONS}
                      value={draft.category}
                      onChange={(category) => setDraftField('category', category)}
                    />
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по источнику</span>
                    <FilterSelect
                      options={toOptions(utmQuery.data)}
                      value={draft.utmId}
                      isLoading={utmQuery.isLoading}
                      onChange={(utmId) => setDraftField('utmId', utmId)}
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по партнеру</span>
                    <FilterSelect
                      options={PARTNER_OPTIONS}
                      value={draft.partnerId}
                      onChange={(partnerId) => setDraftField('partnerId', partnerId)}
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Фильтрация по статусу загрузки</span>
                    <FilterSelect
                      options={LOAD_STATUS_OPTIONS}
                      value={draft.loadStatus}
                      onChange={(loadStatus) => setDraftField('loadStatus', loadStatus)}
                    />
                  </div>
                </div>
              </div>
              <button type="button" className="btn btn-warning" onClick={onReset} disabled={isFetching}>
                Очистить
              </button>{' '}
              <button type="submit" className="btn btn-primary" disabled={isFetching}>
                Найти
              </button>
            </form>
          </div>

          <div className="box-body">
            {isLoading && <p>Загрузка…</p>}
            {error && <div className="alert alert-danger">Ошибка загрузки пользователей</div>}
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-bordered table-hover">
                <thead>
                  <tr>
                    <th className={sortClass('id')}>
                      <a href="#" onClick={(e) => { e.preventDefault(); toggleSort('id'); }}>
                        id
                      </a>
                    </th>
                    <th className={sortClass('name')}>
                      <a href="#" onClick={(e) => { e.preventDefault(); toggleSort('name'); }}>
                        ФИО (Имя)
                      </a>
                    </th>
                    <th>Дата/время регистрации</th>
                    <th>Страна, Город, область</th>
                    <th>Марка/модель (авто)</th>
                    <th className={sortClass('email')}>
                      <a href="#" onClick={(e) => { e.preventDefault(); toggleSort('email'); }}>
                        E-mail
                      </a>
                    </th>
                    <th className={sortClass('phone')}>
                      <a href="#" onClick={(e) => { e.preventDefault(); toggleSort('phone'); }}>
                        Телефон
                      </a>
                    </th>
                    <th>Edit</th>
                    <th>Профиль</th>
                    <th>Баланс</th>
                    <th>Роль</th>
                    <th className={sortClass('status_id')}>
                      <a href="#" onClick={(e) => { e.preventDefault(); toggleSort('status_id'); }}>
                        Статус
                      </a>
                    </th>
                    <th>Тип СТО</th>
                    <th>Устройство</th>
                    <th>Источник</th>
                    <th>Партнер</th>
                    <th>Менеджер</th>
                    <th>Приоритет (для СТО)</th>
                    <th>Коэффициент для работ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => {
                    const id = u.id;
                    const roles = Array.isArray(u.roles) ? u.roles : [];
                    const isService = roles.some((r) => r.value === 'service');
                    const phone = phones[String(id)] ?? u.phone;
                    const profileBase = isService ? __CONFIG__.SERVICE_URL : __CONFIG__.CUSTOMER_URL;
                    const profileUrl = `${profileBase}/cabinet/${isService ? 'service' : 'customer'}/profile`;

                    return (
                      <tr key={String(id)}>
                        <td>
                          <Link to={`/users/user/${id}`}>{id}</Link>
                        </td>
                        <td>
                          <Link to={`/users/user/${id}`}>{u.name || ''}</Link>
                        </td>
                        <td>
                          {u.create_time
                            ? dayjs(
                                typeof u.create_time === 'number' && u.create_time < 1e12
                                  ? u.create_time * 1000
                                  : u.create_time,
                              ).format('DD.MM.YYYY HH:mm')
                            : 'No create time'}
                        </td>
                        <td>{formatPlace(u)}</td>
                        <td>{formatCar(u)}</td>
                        <td>{u.email}</td>
                        <td>
                          {phone ? (
                            phone
                          ) : (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              disabled={phoneMutation.isPending}
                              onClick={() => phoneMutation.mutate(id)}
                            >
                              Показать
                            </button>
                          )}
                        </td>
                        <td>
                          <Link to={`/users/edit/${id}`} className="btn btn-success btn-sm">
                            Edit
                          </Link>
                        </td>
                        <td>
                          <UserProfileRedirect userId={id} route={profileUrl}>
                            <button type="button" className="btn btn-primary btn-sm">
                              Open profile
                            </button>
                          </UserProfileRedirect>
                        </td>
                        <td>{u.balance != null ? parseInt(String(u.balance), 10) : ''}</td>
                        <td>{formatRoles(roles)}</td>
                        <td>{USER_STATUS[u.status_id as number] || ''}</td>
                        <td>{isService ? SERVICE_CATEGORY_LABEL[u.category as number] || '' : ''}</td>
                        <td>{u.source || ''}</td>
                        <td>{u.utm_source || ''}</td>
                        <td>{u.partner_name || ''}</td>
                        <td>{u.manager?.name || 'No manager'}</td>
                        <td>
                          {u.is_priority ? <input type="checkbox" checked readOnly /> : null}
                        </td>
                        <td>{u.rate ?? ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </div>
      </section>
    </>
  );
};
